import { useState } from 'react'
import type { FieldMetaState } from 'react-final-form'

import Button from 'common/components/button'
import { ButtonWithTooltip } from 'common/components/ButtonWithTooltip'
import FormItem from 'common/components/formItem'
import Textarea from 'common/components/textarea/Textarea'
import { successToast } from 'common/components/toastNotification'
import { Bin as BinIcon } from 'common/icons'

const MAX_NAME_LENGTH = 128

type Props = {
  meta: FieldMetaState<unknown>
  exampleInput: string
  exampleOutput: string
  isDeleteDisabled: boolean
  onBlur: () => void
  onChange: ({ value, field }: { value: string; field: string }) => void
  onDelete: () => void
}

const FieldContent = ({
  meta,
  exampleInput,
  exampleOutput,
  isDeleteDisabled,
  onBlur,
  onChange,
  onDelete,
}: Props) => {
  const [isConfirmationVisible, setIsConfirmationVisible] =
    useState<boolean>(false)

  return (
    <div className="w-full flex flex-col justify-between items-center mb-4 rounded-sm  ">
      {isConfirmationVisible ? (
        <div className="w-full bg-gold-200 p-4">
          <div className="flex align-middle flex-col bg-white rounded-sm p-6">
            <p className="font-relais font-medium text-base-700 text-6 text-center pb-6">
              Do you want to remove this example?
            </p>
            <div className="w-full flex justify-center gap-10">
              <Button
                onClick={() => setIsConfirmationVisible(false)}
                variant="icon"
              >
                No, keep it
              </Button>

              <Button
                variant="danger"
                onClick={() => {
                  successToast('Example removed')
                  onDelete()
                  setIsConfirmationVisible(false)
                }}
              >
                Yes, remove it
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col justify-between w-full bg-gold-200 px-4 pt-4 pb-0">
          <div className="flex items-center justify-between group">
            <span className="text-base-700 font-medium">Example:</span>
            {!isDeleteDisabled && (
              <ButtonWithTooltip
                size="small"
                className="invisible group-hover:visible h-auto"
                variant="icon"
                tooltip
                tooltipText="Delete"
                onClick={() => setIsConfirmationVisible(true)}
                prefixIcon={
                  <BinIcon
                    isDefaultColor={false}
                    size={6}
                    className="text-gold-700"
                  />
                }
              />
            )}
          </div>
          <hr className="w-full my-2 border-gold-400" />
          <div className="flex w-full gap-3">
            <FormItem
              label="Do say"
              htmlFor="exampleOutput"
              error={meta.touched ? meta.error : undefined}
            >
              <Textarea
                id="exampleOutput"
                placeholder="Write an example – e.g Your new season's range starts here"
                className="h-25"
                value={exampleOutput}
                onChange={(e) =>
                  onChange({ value: e.target.value, field: 'exampleOutput' })
                }
                onBlur={onBlur}
                showCharacterCount
                maxLength={MAX_NAME_LENGTH}
              />
            </FormItem>

            <FormItem
              label="Don’t say"
              htmlFor="exampleInput"
              error={meta.touched ? meta.error : undefined}
            >
              <Textarea
                id="exampleInput"
                placeholder="Write an example – e.g New season sale starts here"
                className="h-25"
                value={exampleInput}
                onChange={(e) =>
                  onChange({ value: e.target.value, field: 'exampleInput' })
                }
                onBlur={onBlur}
                showCharacterCount
                maxLength={MAX_NAME_LENGTH}
              />
            </FormItem>
          </div>
        </div>
      )}
    </div>
  )
}

export default FieldContent
